import { Icons } from '@/assets/icons/icons';
import { Button, XStack } from '@/components/ui';
import { openSettings } from '@/features/settings/settings-navigation';
import { useIntegrationState } from '@/integrations/useIntegrationState';
import { cn } from '@/lib/utils';

import { useManualCalendarSync } from './use-manual-calendar-sync';

const SYNCABLE_CALENDAR_TOOLKITS = new Set(['googlecalendar', 'outlook']);

type CalendarSyncControlProps = {
  /** Custom reconnect handler; defaults to opening Settings → Integrations. */
  onReconnect?: () => void;
  /** Show the inline "Reconnect calendar" link when accounts are expired/failed. */
  showReconnect?: boolean;
};

export function CalendarSyncControl({
  onReconnect,
  showReconnect = true,
}: CalendarSyncControlProps) {
  const { integrationStates } = useIntegrationState();
  const hasConnectedCalendar = integrationStates.some(
    (state) =>
      state.isConnected &&
      SYNCABLE_CALENDAR_TOOLKITS.has(state.info.toolkit_name)
  );
  const { isSyncing, syncNow, needsReconnect } = useManualCalendarSync();

  if (!hasConnectedCalendar) return null;

  const handleReconnect = () => {
    if (onReconnect) return onReconnect();
    void openSettings('Integrations');
  };

  return (
    <XStack className="items-center gap-1.5">
      {showReconnect && needsReconnect && (
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-foreground-muted hover:text-foreground"
          onClick={handleReconnect}
        >
          Reconnect calendar
        </Button>
      )}
      <Button
        variant="ghost"
        size="icon-sm"
        className={cn(
          'pointer-events-none text-foreground-muted opacity-0 transition-opacity hover:text-foreground',
          'touch:opacity-100 touch:pointer-events-auto',
          'group-hover/calendar-sync:pointer-events-auto group-hover/calendar-sync:opacity-100',
          'group-focus-within/calendar-sync:pointer-events-auto group-focus-within/calendar-sync:opacity-100',
          'focus-visible:pointer-events-auto focus-visible:opacity-100',
          isSyncing && 'pointer-events-auto opacity-100'
        )}
        onClick={syncNow}
        disabled={isSyncing}
        aria-label="Refresh calendar"
        tooltip="Refresh calendar"
      >
        <Icons.refresh size={14} className={cn(isSyncing && 'animate-spin')} />
      </Button>
    </XStack>
  );
}
